import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";

import { LANGUAGE_PAGES, languagePath, type LanguagePage } from "@/lib/languages/content";

import HowItWorks from "@/components/languages/HowItWorks";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import RelatedPages from "@/components/marketing/RelatedPages";
import FAQ from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import FancyButton from "@/components/ui/fancy-button";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { pageGraph } from "@/lib/seo/graph";

interface LanguagePageProps {
  initialPage: LanguagePage | null;
}

const EYEBROW = "text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[#8B93A7]";

const CaptionLanguagePage = ({ initialPage }: LanguagePageProps) => {
  const router = useRouter();
  const slug = typeof router.query.language === "string" ? router.query.language : "";
  const page = initialPage ?? (slug ? (LANGUAGE_PAGES[slug] ?? null) : null);

  if (!page) {
    if (!router.isReady) return null;

    return (
      <div className="flex min-h-[75vh] flex-col items-center justify-center">
        <h1 className="text-4xl font-bold text-gray-900">Page Not Found</h1>
        <p className="mt-4 text-lg text-gray-600">
          The caption language you&apos;re looking for doesn&apos;t exist.
        </p>
        <FancyButton
          href="/captions"
          semiFancy
          className="mt-8 w-fit rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700"
        >
          Browse all languages
        </FancyButton>
      </div>
    );
  }

  const path = languagePath(slug);
  const crumbs = breadcrumbsForPath(path);

  return (
    <div className="flex size-full flex-col overflow-hidden bg-white">
      <NextSeo title={`${page.seoTitle} | Crayo`} description={page.seoDescription} />
      <JsonLd
        nodes={pageGraph({
          path,
          name: page.seoTitle,
          description: page.seoDescription,
          crumbs,
          faqs: page.faqs,
          speakable: [".answer-first"],
        })}
      />
      <Breadcrumbs items={crumbs} />

      <section className="mx-auto flex w-full max-w-screen-2xl flex-col px-4 pb-6 pt-8 md:px-12 md:pt-10 lg:px-[120px]">
        <div className="flex w-full max-w-[760px] flex-col gap-8">
          <div className="flex flex-col gap-3">
            <span className={EYEBROW}>
              {page.nativeName} · {page.script}
            </span>
            <h1 className="text-balance text-3xl font-semibold tracking-tight text-[#101828] sm:text-4xl lg:text-[44px] lg:leading-[1.08]">
              {page.name} Auto Captions
            </h1>
          </div>

          <div className="flex flex-col gap-2 rounded-2xl border border-[#CACFD8] bg-[#F9FBFF] px-5 py-6 sm:px-8 sm:py-7">
            <span className={EYEBROW}>{page.name} captions in short</span>
            <p className="answer-first text-[17px] font-medium leading-[1.6] text-[#101828] sm:text-[19px]">
              {page.answer}
            </p>
          </div>

          <div className="flex flex-col gap-4 text-[15px] leading-[1.7] text-[#525866] sm:text-[16px]">
            {page.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          {page.regions.length > 0 && (
            <div className="flex flex-col gap-3">
              <span className={EYEBROW}>Where {page.name} clips get watched</span>
              <div className="flex flex-wrap gap-2">
                {page.regions.map((region) => (
                  <span
                    key={region}
                    className="rounded-full border border-[#EFF2FF] bg-white px-3.5 py-1.5 text-[13px] font-medium text-[#525866] shadow-sm"
                  >
                    {region}
                  </span>
                ))}
              </div>
            </div>
          )}

          <HowItWorks language={page.name} />

          <div className="flex flex-col gap-3 rounded-2xl border border-[#CACFD8] bg-[#F9FBFF] px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:px-8">
            <span className="text-[15px] font-medium text-[#101828]">
              Clip your next {page.name} episode with captions already on it
            </span>
            <FancyButton
              href="https://crayo.ai/create/auto-clip"
              semiFancy
              cta={`captions-${slug}`}
              className="w-full shrink-0 sm:w-auto sm:px-6"
            >
              Try AutoClip
            </FancyButton>
          </div>
        </div>
      </section>

      <RelatedPages path={path} />
      <FAQ items={page.faqs} />
      <Footer />
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: Object.keys(LANGUAGE_PAGES).map((language) => ({ params: { language } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<LanguagePageProps> = async ({ params }) => {
  const slug = params?.language as string;
  const initialPage = LANGUAGE_PAGES[slug] || null;

  return { props: { initialPage } };
};

export default CaptionLanguagePage;
